/**
 * A panel that opens under a control and is clipped by nothing.
 *
 * ## The bug this exists to remove
 *
 * A dropdown drawn as `position: absolute` inside its own row is clipped by
 * every scrolling or hidden ancestor between it and the page. On the Review
 * queue that was two layers deep: the record card was `overflow: hidden`, and
 * the card list it sits in is `overflow: auto`. Opening a field's dropdown
 * near the bottom of the pane produced a panel that rendered correctly,
 * reported itself open, and could not be seen — "the review dropdown isn't
 * working".
 *
 * Removing the card's clip fixed the inner layer and left the outer one, which
 * is the shape of the whole problem: any fix that lives inside the tree can be
 * re-broken by the next ancestor somebody adds an `overflow` to. So the panel
 * leaves the tree. It is portalled to `<body>` and positioned `fixed` from the
 * anchor's own measured rect, which no ancestor can clip.
 *
 * ## Positioning is measured, and re-measured
 *
 * `fixed` means viewport coordinates, so a panel pinned once would drift the
 * moment anything scrolled. The listener is registered in the CAPTURE phase,
 * which is the only way to hear scroll from an inner container — scroll does
 * not bubble.
 *
 * It flips above the anchor when there is not room below, so a control near
 * the foot of the pane opens upward rather than off-screen.
 *
 * It is re-measured when the PANEL changes size too, not only on scroll and
 * resize. A panel opened before its rows arrive grows when they land, and a
 * right-aligned panel grows LEFTWARD: measured once at 222px wide and then
 * filled to 300, the sidebar's project menu sat 64px off the left edge of the
 * screen until something scrolled.
 *
 * ## What it deliberately does not do
 *
 * No focus trap, no arrow-key roving. These panels are short lists opened from
 * a button that keeps focus; the caller supplies real `<button role="option">`
 * children, so tab order already works and `Escape` closes. A trap here would
 * be a second, worse implementation of what the browser does.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { C } from "../tokens";

/** Gap between the anchor and the panel, both directions. */
const OFFSET = 7;
/** How close to the viewport edge the panel may come before it flips. */
const EDGE = 12;

export interface AnchoredPanelProps {
  /** The control the panel hangs off. Measured, never mutated. */
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  children: React.ReactNode;
  /** Announced to assistive tech; the caller owns `role`. */
  label: string;
  role?: "listbox" | "dialog" | "menu";
  minWidth?: number;
  maxWidth?: number;
  testId?: string;
  /**
   * Which edge of the anchor the panel lines up with. `"right"` (the default)
   * suits controls at the right of a row; `"left"` suits a full-width field,
   * whose menu should hang from the field's own left edge.
   */
  align?: "left" | "right";
  /**
   * Chrome overrides, merged over the panel's own.
   *
   * The panel owns POSITION — the whole reason it exists is that no ancestor
   * may clip or misplace it — so `position`, `top`, `left`/`right`, `zIndex` and
   * `visibility` are set after this and cannot be overridden. Everything
   * cosmetic can: turn 13's pickers carry a lighter edge and a two-layer
   * shadow than Review's dropdowns, and a picker that looked like a dropdown
   * would undo the point of a properties column that looks like nothing.
   */
  panelStyle?: React.CSSProperties;
}

export function AnchoredPanel({
  anchorRef,
  onClose,
  children,
  label,
  role = "listbox",
  minWidth = 190,
  maxWidth = 300,
  testId,
  align = "right",
  panelStyle,
}: AnchoredPanelProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<{ top: number; right?: number; left?: number; flipped: boolean } | null>(
    null,
  );

  // `useLayoutEffect`, not `useEffect`: the panel must be positioned in the
  // same frame it is painted, or it appears at 0,0 for one frame and jumps.
  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const panelHeight = panelRef.current?.offsetHeight ?? 0;
      const panelWidth = panelRef.current?.offsetWidth ?? 0;
      const below = window.innerHeight - rect.bottom - OFFSET - EDGE;
      const flipped = panelHeight > below && rect.top > below;
      setBox({
        top: flipped ? rect.top - OFFSET - panelHeight : rect.bottom + OFFSET,
        ...(align === "left"
          ? { left: Math.max(EDGE, Math.min(rect.left, window.innerWidth - panelWidth - EDGE)) }
          : {
              // Right alignment can push a wide menu off the left edge when its
              // trigger is in the sidebar. Keep both edges inside the viewport.
              right: Math.max(EDGE, Math.min(
                window.innerWidth - rect.right,
                window.innerWidth - panelWidth - EDGE,
              )),
            }),
        flipped,
      });
    };

    place();
    // The panel's own size changes (rows arriving, a search narrowing the
    // list) move where it must sit; see the header.
    const observer =
      typeof ResizeObserver === "undefined" || !panelRef.current
        ? null
        : new ResizeObserver(() => place());
    if (observer && panelRef.current) observer.observe(panelRef.current);
    // Capture, because scroll does not bubble: without it a panel opened
    // inside the review pane would stay where the page was when it opened.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchorRef, align]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  // `document.body` is read at render rather than module scope: this module is
  // imported in a test process where the DOM may not exist yet.
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={panelRef}
      role={role}
      aria-label={label}
      data-testid={testId}
      data-flipped={box?.flipped ? "true" : "false"}
      style={{
        boxSizing: "border-box",
        minWidth: `min(${minWidth}px, calc(100vw - ${EDGE * 2}px))`,
        maxWidth: `min(${maxWidth}px, calc(100vw - ${EDGE * 2}px))`,
        width: "max-content",
        maxHeight: "min(60vh, 420px)",
        overflowY: "auto",
        padding: 5,
        border: `1px solid ${C.line2}`,
        borderRadius: 12,
        background: C.surface,
        boxShadow: "0 14px 30px rgba(22, 18, 10, 0.16)",
        animation: "ledai-review-dropdown-in 140ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        ...panelStyle,
        // AFTER the overrides, deliberately: the panel owns where it is. A
        // caller that could set `position` or `top` could reintroduce the
        // clipping bug this whole component exists to remove.
        position: "fixed",
        top: box?.top ?? -9999,
        ...(box?.left !== undefined ? { left: box.left } : { right: box?.right ?? 0 }),
        // Hidden until measured, so it cannot flash at the wrong place.
        visibility: box ? "visible" : "hidden",
        zIndex: 60,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
